import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Badge, OverlayTrigger, Tooltip, Modal, ProgressBar,
    InputGroup, Form, Pagination
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function SauvegardeManuel() {
    const navigate = useNavigate();
    const [sauvegardes, setSauvegardes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    
    // États pour la recherche
    const [searchText, setSearchText] = useState('');

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    
    // États pour les modales de confirmation
    const [showBackupModal, setShowBackupModal] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        fetchSauvegardes();
    }, []);

    const fetchSauvegardes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=sauvegardes',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setSauvegardes(result.data || []);
            setCurrentPage(1); // Reset à la première page
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleBackupConfirm = async () => {
        setShowBackupModal(false);
        setSaving(true);
        setError(null);
        setSuccess('');
        setProgress(0);
        
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 90) {
                    clearInterval(interval);
                    return 90;
                }
                return prev + 10;
            });
        }, 300);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php',
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'creer_sauvegarde'
                    })
                }
            );
            
            if (!response.ok) {
                const text = await response.text();
                console.error("Réponse non-OK:", response.status, text);
                throw new Error(`Erreur HTTP ${response.status}: ${text.substring(0, 100)}`);
            }
            
            const text = await response.text();
            console.log("Réponse brute:", text);
            
            let result;
            try {
                result = JSON.parse(text);
            } catch (e) {
                console.error("Erreur de parsing JSON:", e);
                throw new Error("La réponse du serveur n'est pas au format JSON valide");
            }
            
            clearInterval(interval);
            setProgress(100);
            
            if (result.success) {
                setSuccess("✅ Sauvegarde créée avec succès");
                setTimeout(() => setSuccess(''), 3000);
                fetchSauvegardes();
            } else {
                setError(result.message || "Erreur lors de la sauvegarde");
            }
        } catch (err) {
            clearInterval(interval);
            console.error("Erreur complète:", err);
            setError(err.message);
        } finally {
            setSaving(false);
            setProgress(0);
        }
    };

    const handleRestoreConfirm = async () => {
        setShowRestoreModal(false);
        setRestoring(true);
        setError(null);
        setSuccess('');
        setProgress(0);
        
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 90) {
                    clearInterval(interval);
                    return 90;
                }
                return prev + 10;
            });
        }, 500);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php',
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'restaurer_sauvegarde',
                        fichier: selectedFile
                    })
                }
            );
            
            const result = await response.json();
            clearInterval(interval);
            setProgress(100);
            
            if (result.success) {
                setSuccess("🔄 Restauration réussie ! La page va se recharger...");
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            } else {
                setError(result.message || "Erreur lors de la restauration");
            }
        } catch (err) {
            clearInterval(interval);
            setError(err.message);
        } finally {
            setRestoring(false);
            setProgress(0);
        }
    };

    const handleDeleteConfirm = async () => {
        setShowDeleteModal(false);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/parametres.php?action=delete_sauvegarde&fichier=${encodeURIComponent(selectedFile)}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setSuccess("🗑️ Sauvegarde supprimée");
                fetchSauvegardes();
            } else {
                setError("Erreur lors de la suppression");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setTimeout(() => setSuccess(''), 3000);
        }
    };

    const formatTaille = (octets) => {
        if (octets < 1024) return octets + ' o';
        if (octets < 1024 * 1024) return (octets / 1024).toFixed(1) + ' Ko';
        return (octets / (1024 * 1024)).toFixed(1) + ' Mo';
    };

    // Filtrage des sauvegardes
    const filteredSauvegardes = useMemo(() => {
        return sauvegardes.filter(s => 
            s.nom?.toLowerCase().includes(searchText.toLowerCase()) ||
            formatDate(s.date).toLowerCase().includes(searchText.toLowerCase())
        );
    }, [sauvegardes, searchText]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredSauvegardes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredSauvegardes.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des sauvegardes...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))',
                                            boxShadow: '0 10px 20px rgba(16, 185, 129, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-database-2-line text-success" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Sauvegarde manuelle
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Créer et gérer les sauvegardes de la base de données
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Alertes modernes */}
                    {success && (
                        <Alert 
                            variant="success" 
                            onClose={() => setSuccess('')} 
                            dismissible 
                            className="mb-4 alert-modern fade-in"
                            style={{
                                borderRadius: '12px',
                                borderLeft: '4px solid #10b981',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                                animation: 'slideInDown 0.3s ease-out'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <div className="bg-success bg-opacity-10 rounded-circle p-2 me-3">
                                    <i className="ri-checkbox-circle-line fs-4 text-success"></i>
                                </div>
                                <div>
                                    <strong className="text-success d-block">Succès</strong>
                                    <p className="mb-0">{success}</p>
                                </div>
                            </div>
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert 
                            variant="danger" 
                            onClose={() => setError(null)} 
                            dismissible 
                            className="mb-4 alert-modern fade-in"
                            style={{
                                borderRadius: '12px',
                                borderLeft: '4px solid #dc3545',
                                boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2)',
                                animation: 'slideInDown 0.3s ease-out'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <div className="bg-danger bg-opacity-10 rounded-circle p-2 me-3">
                                    <i className="ri-error-warning-line fs-4 text-danger"></i>
                                </div>
                                <div>
                                    <strong className="text-danger d-block">Erreur</strong>
                                    <p className="mb-0">{error}</p>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {/* Barre de progression */}
                    {(saving || restoring) && (
                        <Card className="border-0 shadow-sm mb-4 bg-light">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                                        <Spinner animation="border" size="sm" variant="primary" />
                                    </div>
                                    <div>
                                        <strong className="d-block">
                                            {saving ? 'Sauvegarde en cours...' : 'Restauration en cours...'}
                                        </strong>
                                        <small className="text-muted">Cette opération peut prendre quelques instants</small>
                                    </div>
                                </div>
                                <ProgressBar 
                                    animated 
                                    now={progress} 
                                    variant={saving ? 'success' : 'warning'}
                                    style={{ height: '8px', borderRadius: '4px' }}
                                />
                            </Card.Body>
                        </Card>
                    )}

                    {/* Actions */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="p-4 text-center">
                            <div className="bg-success-soft rounded-circle d-inline-flex p-3 mb-3">
                                <i className="ri-database-2-line text-success fs-1"></i>
                            </div>
                            <h4 className="mb-2">Créer une nouvelle sauvegarde</h4>
                            <p className="text-muted mb-4" style={{ maxWidth: '500px', margin: '0 auto' }}>
                                Cette action va sauvegarder l'intégralité de la base de données dans un fichier SQL sécurisé.
                            </p>
                            <Button 
                                variant="success" 
                                size="lg"
                                onClick={() => setShowBackupModal(true)}
                                disabled={saving || restoring}
                                className="px-5 py-3 rounded-pill d-inline-flex align-items-center gap-2"
                                style={{
                                    background: 'linear-gradient(135deg, #10b981, #059669)',
                                    border: 'none',
                                    boxShadow: '0 8px 16px rgba(16, 185, 129, 0.25)'
                                }}
                            >
                                <i className="ri-database-2-line fs-5"></i>
                                <span>Lancer la sauvegarde</span>
                            </Button>
                        </Card.Body>
                    </Card>

                    {/* Barre de recherche */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-center">
                                <Col md={8}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <i className="ri-search-line text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher un fichier ou une date..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            className="border-start-0"
                                        />
                                        {searchText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setSearchText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={4} className="text-end">
                                    <Badge bg="primary" className="px-3 py-2 rounded-pill">
                                        {filteredSauvegardes.length} fichier(s) trouvé(s)
                                    </Badge>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des sauvegardes avec pagination */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="ri-history-line me-2 text-primary"></i>
                                Historique des sauvegardes
                            </h5>
                            <Badge bg="primary" className="px-3 py-2 rounded-pill">
                                {filteredSauvegardes.length} fichier(s)
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Date</th>
                                            <th style={{minWidth:'190px'}}>Fichier</th>
                                            <th>Taille</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((s, index) => (
                                                <tr key={index} style={{
                                                    animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                }}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <i className="ri-calendar-line me-2 text-muted"></i>
                                                            {formatDate(s.date)}
                                                        </div>
                                                        <small className="text-muted">
                                                            <i className="ri-time-line me-1"></i>
                                                            {new Date(s.date).toLocaleTimeString('fr-FR')}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <code className="bg-light p-2 rounded d-inline-block">
                                                            {s.nom}
                                                        </code>
                                                    </td>
                                                    <td>
                                                        <Badge bg="secondary" className="rounded-pill px-3 py-2">
                                                            {formatTaille(s.taille)}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-center">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Restaurer cette sauvegarde</Tooltip>}>
                                                            <Button 
                                                                variant="outline-warning" 
                                                                size="sm"
                                                                className="rounded-circle me-2"
                                                                style={{ width: '38px', height: '38px' }}
                                                                onClick={() => {
                                                                    setSelectedFile(s.nom);
                                                                    setShowRestoreModal(true);
                                                                }}
                                                                disabled={restoring || saving}
                                                            >
                                                                <i className="ri-restart-line"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                                                            <Button 
                                                                variant="outline-danger" 
                                                                size="sm"
                                                                className="rounded-circle"
                                                                style={{ width: '38px', height: '38px' }}
                                                                onClick={() => {
                                                                    setSelectedFile(s.nom);
                                                                    setShowDeleteModal(true);
                                                                }}
                                                                disabled={restoring || saving}
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-inbox-line fs-1 text-muted mb-3"></i>
                                                        <h5>Aucune sauvegarde trouvée</h5>
                                                        <p className="text-muted">
                                                            {searchText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Créez votre première sauvegarde pour protéger vos données"}
                                                        </p>
                                                        {searchText && (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setSearchText('')}
                                                                className="mt-2"
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredSauvegardes.length)} sur {filteredSauvegardes.length} sauvegardes
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal de confirmation - Sauvegarde */}
                <Modal show={showBackupModal} onHide={() => setShowBackupModal(false)} centered className="confirm-modal">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <div className="icon-wrapper bg-success-soft">
                                        <i className="ri-database-2-line text-success" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                </div>
                                <h3 className="fw-bold text-success">Confirmer la sauvegarde</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center pt-2">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir créer une sauvegarde de la base de données ?
                        </p>
                        <Alert variant="info" className="text-start mb-4">
                            <div className="d-flex">
                                <i className="ri-information-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Bon à savoir</strong>
                                    <p className="mb-0 small mt-1">
                                        La sauvegarde sera stockée sur le serveur et pourra être restaurée ultérieurement.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowBackupModal(false)} className="px-4 rounded-pill">
                            <i className="ri-close-line me-2"></i>Annuler
                        </Button>
                        <Button 
                            variant="success" 
                            onClick={handleBackupConfirm} 
                            className="px-5 rounded-pill"
                            style={{
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                border: 'none'
                            }}
                        >
                            <i className="ri-check-line me-2"></i>Confirmer
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal de confirmation - Restauration */}
                <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)} centered className="confirm-modal">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <div className="icon-wrapper bg-warning-soft">
                                        <i className="ri-restart-line text-warning" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                </div>
                                <h3 className="fw-bold text-warning">⚠️ Attention</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center pt-2">
                        <p className="text-muted mb-4">
                            Vous êtes sur le point de restaurer la sauvegarde <strong>"{selectedFile}"</strong>
                        </p>
                        <Alert variant="danger" className="text-start mb-4">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible !</strong>
                                    <p className="mb-0 small mt-1">
                                        La restauration va écraser TOUTES les données actuelles par celles de la sauvegarde.
                                        Assurez-vous d'avoir une sauvegarde récente de vos données actuelles.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowRestoreModal(false)} className="px-4 rounded-pill">
                            <i className="ri-close-line me-2"></i>Annuler
                        </Button>
                        <Button 
                            variant="warning" 
                            onClick={handleRestoreConfirm} 
                            className="px-5 rounded-pill"
                            style={{
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                border: 'none',
                                color: 'white'
                            }}
                        >
                            <i className="ri-restart-line me-2"></i>Restaurer
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal de confirmation - Suppression */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="confirm-modal">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <div className="icon-wrapper bg-danger-soft">
                                        <i className="ri-delete-bin-line text-danger" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                </div>
                                <h3 className="fw-bold text-danger">Supprimer la sauvegarde</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center pt-2">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir supprimer la sauvegarde <strong>"{selectedFile}"</strong> ?
                        </p>
                        <Alert variant="warning" className="text-start mb-4">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible</strong>
                                    <p className="mb-0 small mt-1">
                                        Le fichier sera définitivement supprimé du serveur.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="px-4 rounded-pill">
                            <i className="ri-close-line me-2"></i>Annuler
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleDeleteConfirm} 
                            className="px-5 rounded-pill"
                            style={{
                                background: 'linear-gradient(135deg, #dc3545, #bb2d3b)',
                                border: 'none'
                            }}
                        >
                            <i className="ri-delete-bin-line me-2"></i>Supprimer
                        </Button>
                    </Modal.Footer>
                </Modal>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    @keyframes slideInDown {
                        from { transform: translateY(-20px); opacity: 0; }
                        to { transform: translateY(0); opacity: 1; }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .alert-modern {
                        border: none;
                        padding: 1rem 1.5rem;
                    }

                    .alert-modern .btn-close {
                        padding: 1.2rem 1.5rem;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .bg-success-soft {
                        background: rgba(16, 185, 129, 0.15);
                    }

                    .bg-warning-soft {
                        background: rgba(245, 158, 11, 0.15);
                    }

                    .bg-danger-soft {
                        background: rgba(220, 53, 69, 0.15);
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .search-input .form-control:focus {
                        box-shadow: none;
                        border-color: #dee2e6;
                    }

                    /* Styles pour la pagination */
                    .pagination {
                        gap: 5px;
                    }
                    
                    .pagination .page-item .page-link {
                        border-radius: 8px;
                        border: 1px solid #e2e8f0;
                        color: #1e293b;
                        padding: 0.5rem 1rem;
                        transition: all 0.2s ease;
                        font-weight: 500;
                    }
                    
                    .pagination .page-item.active .page-link {
                        background: linear-gradient(135deg, #4361ee, #3a56d4);
                        border-color: #4361ee;
                        color: white;
                        box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                    }
                    
                    .pagination .page-item .page-link:hover {
                        background: #f1f5f9;
                        transform: translateY(-2px);
                        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    }
                    
                    .pagination .page-item.disabled .page-link {
                        opacity: 0.5;
                        pointer-events: none;
                    }

                    /* Styles des modales */
                    .confirm-modal .modal-content {
                        border: none;
                        border-radius: 24px;
                        box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                        overflow: hidden;
                    }

                    .confirm-modal .modal-header {
                        background: linear-gradient(135deg, #f8fafc, #ffffff);
                        border-bottom: none;
                        padding: 2rem 1.5rem 0.5rem;
                    }

                    .confirm-modal .modal-body {
                        padding: 1rem 2rem;
                    }

                    .confirm-modal .modal-footer {
                        border-top: 1px solid #e2e8f0;
                        padding: 1.5rem 2rem 2rem;
                        background: #f8fafc;
                    }

                    .icon-wrapper {
                        width: 100px;
                        height: 100px;
                        margin: 0 auto;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 50%;
                        animation: pulse 2s infinite;
                    }

                    .confirm-icon {
                        animation: bounceIn 0.5s ease-out;
                    }

                    @keyframes bounceIn {
                        0% { transform: scale(0); opacity: 0; }
                        50% { transform: scale(1.1); }
                        100% { transform: scale(1); opacity: 1; }
                    }

                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                        100% { transform: scale(1); }
                    }

                    .confirm-modal .btn {
                        border-radius: 12px;
                        font-weight: 600;
                        padding: 0.6rem 1.5rem;
                        transition: all 0.3s ease;
                    }

                    .confirm-modal .btn:hover:not(:disabled) {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                    }

                    .confirm-modal .alert {
                        border: none;
                        border-radius: 12px;
                        background: linear-gradient(135deg, #f8fafc, #ffffff);
                        border-left: 4px solid;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}